import { Component, inject, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { switchMap } from 'rxjs/operators';

import { RouterLinkFactory } from './core/router/router-link.factory';
import { UserService } from './core/services/user.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  title = 'management-app';

  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly titleService = inject(Title);
  private readonly translocoService = inject(TranslocoService);
  private readonly links = inject(RouterLinkFactory);

  ngOnInit(): void {
    /**
     * Workaround for Android devices.
     *
     * If app is in the background for a long time, the page is white / blank when it comes back to the foreground.
     * This is a workaround to refresh the page when the app comes back to the foreground.
     */
    try {
      document.addEventListener('visibilitychange', () => {
        const disallowedRoutes = [
          this.links.createAnnouncement().join('/'),
          this.links.createGradebook().join('/'),
          this.links.createChild().join('/'),
          this.links.createCloseRelative('').join('/'),
          this.links.createGroup().join('/'),
          this.links.createHolidaysDates().join('/'),
        ];

        const visibility: DocumentVisibilityState = document.visibilityState;
        if (
          visibility === 'visible' &&
          this._isAndroid() &&
          !this._isCreateOrEditForm() &&
          !disallowedRoutes.some((route) => this.router.url.includes(route))
        ) {
          window.location.reload();
        }
      });
    } catch (error) {
      console.error(error);
    }

    this.userService.loggedKindergarten$
      .pipe(
        switchMap((kindergarten) => {
          const key = kindergarten?.isDemo ? 'demo/title' : 'shared/appTitle';
          return this.translocoService.selectTranslate(key);
        })
      )
      .subscribe((translatedTitle) => {
        this.titleService.setTitle(translatedTitle);
      });
  }

  /**
   * Determine the mobile Android operating system.
   * This function returns boolean if the user is using an Android device.
   *
   * @returns {boolean}
   */
  private _isAndroid(): boolean {
    const userAgent = navigator.userAgent;

    if (/android/i.test(userAgent)) {
      return true;
    }

    return false;
  }

  private _isCreateOrEditForm(): boolean {
    const url = this.router.url;
    return url.includes('new') || url.includes('edit');
  }
}
